import { useState, useCallback } from "react";
import {
  computeCompletion,
  isAllSectionsAnswered,
} from "../utils/assessmentHelpers";

export const useAssessment = (onSave) => {
  const [selectedType, setSelectedType] = useState(null);
  const [toolName, setToolName]         = useState("");
  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);
  const [answers, setAnswers]           = useState({});
  const [editingId, setEditingId]       = useState(null);
  const [formId, setFormId]             = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  const [apiSections, setApiSections]   = useState([]);

  const sections       = apiSections.map((s) => s.section);
  const reportIdx      = sections.indexOf("Report");
  const completion     = apiSections.length ? computeCompletion(apiSections, answers) : 0;
  const reportUnlocked = apiSections.length ? isAllSectionsAnswered(apiSections, answers) : false;

  const updateAnswer = useCallback((sectionName, questionIdx, field, value) => {
    setAnswers((prev) => {
      const section = [...(prev[sectionName] || [])];
      section[questionIdx] = { ...section[questionIdx], [field]: value };
      return { ...prev, [sectionName]: section };
    });
  }, []);

  const goToSection = useCallback(
    (idx) => {
      const isReport = idx === reportIdx;
      if (isReport && !reportUnlocked) return;
      setCurrentSectionIdx(idx);
    },
    [reportIdx, reportUnlocked]
  );

  const goNext = useCallback(() => {
    setCurrentSectionIdx((c) => {
      const next = c + 1;
      if (next >= sections.length) return c;
      const isReport = next === reportIdx;
      if (isReport && !reportUnlocked) return c;
      return next;
    });
  }, [sections.length, reportIdx, reportUnlocked]);

  const goPrev = useCallback(() => {
    if (currentSectionIdx > 0) setCurrentSectionIdx((c) => c - 1);
  }, [currentSectionIdx]);

  /** POST /form/create-form */
  const startNew = useCallback(async (type, tool) => {
    setSelectedType(type);
    setToolName(tool);
    setCurrentSectionIdx(0);
    setEditingId(null);
    setFormId(null);
    setError(null);
    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/form/create-form`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tool_name: type,
          type_name: tool,
          user_id:   localStorage.getItem("userinfo") || "",
        }),
      });

      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const data = await res.json();

      const newFormId = data.form_id;
      setFormId(newFormId);

      const fetchedSections = data.structure?.sections || [];
      setApiSections(fetchedSections);

      const answersFromApi = Object.fromEntries(
        fetchedSections.map((sec) => [
          sec.section,
          sec.questions.map((q) => ({
            id:       q.id,
            response: q.response,
            comment:  q.comment || "",
          })),
        ])
      );
      setAnswers(answersFromApi);

    } catch (err) {
      console.error("Failed to create form:", err);
      setError("Failed to load questions. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  /** Load an existing record for editing */
  const loadRecord = useCallback((record) => {
    setSelectedType(record.type);
    setToolName(record.toolName);
    setApiSections(record.apiSections || []);
    setAnswers(record.answers);
    setCurrentSectionIdx(0);
    setEditingId(record.id);
    setFormId(record.id);
  }, []);

  /** POST /form/submit — ✅ now THROWS on error so App.jsx can catch it */
  const save = useCallback(
    async (status) => {
      const payload = {
        structure: {
          tool:      selectedType,
          tool_type: toolName,
          form_id:   formId,
          sections:  apiSections.map((sec) => ({
            section:   sec.section,
            questions: (answers[sec.section] || [])
              .filter((a) => a.response !== null)
              .map((a) => ({
                id:       a.id,
                response: a.response,
                comment:  a.comment || "",
              })),
          })),
        },
        user_id: localStorage.getItem("userinfo") || "",
        status:  status === "Draft" ? "draft" : "submitted",
      };

      const res = await fetch(`${process.env.REACT_APP_API_URL}/form/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // ✅ Throw so App.jsx catch block can show the error toast
      if (!res.ok) throw new Error(`Submit failed: ${res.status}`);

      const responseData = await res.json().catch(() => null);

      const returnedFormId = responseData?.form_id || formId;
      if (returnedFormId && returnedFormId !== formId) {
        setFormId(returnedFormId);
      }

      onSave({
        id:          returnedFormId,
        type:        selectedType,
        toolName,
        answers,
        apiSections,
        completion,
        status,
        submittedBy: localStorage.getItem("userinfo") || "",
        savedAt:     new Date().toLocaleString(),
      });
    },
    [formId, selectedType, toolName, answers, apiSections, completion, onSave]
  );

  return {
    selectedType,
    toolName,
    currentSectionIdx,
    answers,
    sections,
    apiSections,
    reportIdx,
    completion,
    reportUnlocked,
    loading,
    error,
    formId,
    setSelectedType,
    setToolName,
    updateAnswer,
    goToSection,
    goNext,
    goPrev,
    startNew,
    loadRecord,
    save,
  };
};